import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import type { BaseVideoFormData, ITraining } from '../../types';
import {
    fetchInstructionVideos,
    uploadInstructionVideo,
    editInstructionVideo,
    deleteInstructionVideo,
    setSelectedVideo,
    setOpenDialog,
    setDeleteDialog,
    updateVideoFromStatus
} from '../slices/instructionSlice';
import { useNotification } from '../../components/shared/NotificationSystem';
import { useTranslation } from 'react-i18next';
import { useLanguage as useLanguageContext } from '../../i18n/LanguageContext';
import { getCookie } from '../../utils/cookieUtils';

export const useInstruction = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { t, i18n } = useTranslation();
    const { language: contextLanguage } = useLanguageContext();
    const { showSuccess, showError, showWarning } = useNotification();

    const {
        videos,
        selectedVideo,
        isLoading,
        error,
        deleteDialog,
        openDialog
    } = useSelector((state: RootState) => state.instruction);

    // Используем язык из контекста или куки вместо admin_language
    const language = contextLanguage || getCookie('language') || getCookie('i18next') || 'ru';

    // Load instruction videos
    const loadInstructionVideos = useCallback(() => {
        // Синхронизируем язык i18n с текущим языком приложения
        if (i18n.language !== language) {
            i18n.changeLanguage(language);
        }
        dispatch(fetchInstructionVideos(language));
    }, [dispatch, language, i18n]);

    // Handle form submission for adding/editing
    const handleSubmit = useCallback(async (data: BaseVideoFormData) => {
        try {
            // If we have a video, we're editing, otherwise we're creating
            if (videos.length > 0) {
                await dispatch(editInstructionVideo({
                    id: videos[0].id,
                    training: data.training,
                    language,
                    videoFile: data.videoFile,
                    thumbnailFile: data.thumbnailFile
                }));
                showSuccess(t('content.instruction.success.updated'));
                return null; // Редактирование не создает новое видео
            } else {
                const result = await dispatch(uploadInstructionVideo({
                    training: data.training,
                    language,
                    videoFile: data.videoFile,
                    thumbnailFile: data.thumbnailFile
                }));
                showSuccess(t('content.instruction.success.saved'));
                return result; // Возвращаем результат для отслеживания нового видео
            }
        } catch (error) {
            console.error('Error uploading instruction video:', error);
            showError(t('content.instruction.errors.uploadFailed'));
            throw error;
        }
    }, [dispatch, videos, language, t, showSuccess, showError]);

    // Handle delete with language check
    const handleDelete = useCallback((id: number) => {
        try {
            const video = videos.find(v => v.id === id);
            if (!video) return;

            if (video.language && video.language.toUpperCase() !== language.toUpperCase()) {
                showWarning(t('content.instruction.warnings.wrongLanguage', {
                    videoLang: video.language,
                    currentLang: language.toUpperCase()
                }));
                return;
            }

            dispatch(setDeleteDialog({
                open: true,
                videoId: id
            }));
        } catch (error) {
            console.error('Error preparing delete:', error);
            showError(t('content.instruction.errors.deleteFailed'));
        }
    }, [dispatch, videos, language, t, showWarning, showError]);

    // Confirm video deletion
    const confirmDelete = useCallback(async () => {
        if (!deleteDialog.videoId) return;

        try {
            await dispatch(deleteInstructionVideo(deleteDialog.videoId));
            showSuccess(t('content.instruction.success.deleted'));
        } catch (error) {
            console.error('Error deleting video:', error);
            showError(t('content.instruction.errors.deleteFailed'));
        }
    }, [dispatch, deleteDialog.videoId, t, showSuccess, showError]);

    // Video preview control
    const handleVideoClick = useCallback((video: ITraining) => {
        dispatch(setSelectedVideo(video));
    }, [dispatch]);

    const closeVideoPreview = useCallback(() => {
        dispatch(setSelectedVideo(null));
    }, [dispatch]);

    // Dialog controls
    const handleOpenDialog = useCallback(() => {
        dispatch(setOpenDialog(true));
    }, [dispatch]);

    const closeDialog = useCallback(() => {
        dispatch(setOpenDialog(false));
    }, [dispatch]);

    const closeDeleteDialog = useCallback(() => {
        dispatch(setDeleteDialog({ open: false, videoId: null }));
    }, [dispatch]);

    const handleUpdateVideoFromStatus = useCallback((videoId: number, updates: Partial<ITraining>) => {
        dispatch(updateVideoFromStatus({ videoId, updates }));
    }, [dispatch]);

    return {
        // State
        videos,
        selectedVideo,
        isLoading,
        error,
        deleteDialog,
        openDialog,
        language,

        // Computed
        video: videos.length > 0 ? videos[0] : null,
        hasVideo: videos.length > 0,

        // Actions
        loadInstructionVideos,
        handleSubmit,
        handleDelete,
        confirmDelete,
        handleVideoClick,
        closeVideoPreview,
        handleOpenDialog,
        closeDialog,
        closeDeleteDialog,
        updateVideoFromStatus: handleUpdateVideoFromStatus
    };
}; 